// ─── TabRubrosAnuales — pegar en Empleados.jsx ───────────────────────────────
// Importar junto a los otros tabs. Se usa como tab global (sin empleado seleccionado).
// Requiere: api de ../api

import { useState, useEffect, useCallback } from "react";
import { api } from "../api";

const RUBROS_META = {
  decimo_tercero: { label: "Décimo Tercero", icon: "🎄", color: "#4cc9f0", mes: "Dic" },
  decimo_cuarto:  { label: "Décimo Cuarto",  icon: "📚", color: "#f72585", mes: "Mar (Costa)" },
  utilidades:     { label: "Utilidades",     icon: "💰", color: "#4caf50", mes: "Abr" },
  vacaciones:     { label: "Vacaciones",     icon: "🏖️", color: "#ffc107", mes: "Variable" },
};

const FORMAS_PAGO = [
  { key: "vale_caja",     label: "Vale de caja",   icon: "🧾" },
  { key: "cheque",        label: "Cheque",          icon: "📄" },
  { key: "transferencia", label: "Transferencia",   icon: "🔀" },
  { key: "efectivo",      label: "Efectivo",        icon: "💵" },
];

function fmt(n) { return Number(n || 0).toFixed(2); }

// ─── Barra de progreso ────────────────────────────────────────────────────────
function BarraProgreso({ pagado, total, color }) {
  const pct = total > 0 ? Math.min(Math.round((pagado / total) * 100), 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ flex: 1, height: 6, background: "#2a2a3e",
        borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%",
          background: pct === 100 ? "#4caf50" : color,
          borderRadius: 3, transition: "width .4s" }} />
      </div>
      <span style={{ color: pct === 100 ? "#4caf50" : "#555",
        fontSize: 10, fontFamily: "monospace", minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

// ─── Modal asignar vale ───────────────────────────────────────────────────────
function ModalAsignarVale({ cedula, nombre, anio, onClose, onAsignado }) {
  const [vales,    setVales]    = useState(null);
  const [rubro,    setRubro]    = useState("decimo_tercero");
  const [selected, setSelected] = useState(null);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    api.get(`/empleados/${cedula}/vales-rubro?anio=${anio}`).then(setVales);
  }, [cedula, anio]);

  async function handleAsignar() {
    if (!selected) return;
    setSaving(true);
    const vale = vales.vales.find(v => v.id === selected);
    await api.post("/empleados/rubros/pago", {
      cedula, anio, rubro,
      descripcion: vale.descripcion || `Vale #${vale.numero}`,
      valor:       vale.valor,
      forma_pago:  "vale_caja",
      referencia:  `Vale #${vale.numero}`,
      vale_id:     vale.id,
    });
    setSaving(false);
    onAsignado();
    onClose();
  }

  const rubroSugerido = selected
    ? vales?.vales?.find(v => v.id === selected)?.rubro_sugerido
    : null;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)", zIndex: 200 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "#1e1e2e", borderRadius: 12, padding: 28,
        width: 500, zIndex: 201, border: "1px solid #2a2a3e",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        display: "flex", flexDirection: "column", gap: 16,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center" }}>
          <div>
            <div style={{ color: "#4cc9f0", fontWeight: 700, fontSize: 15 }}>
              Asignar vale a rubro
            </div>
            <div style={{ color: "#555", fontSize: 12, marginTop: 2 }}>{nombre}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: "#555", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {/* Selector de rubro destino */}
        <div>
          <div style={{ color: "#aaa", fontSize: 11, textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 8 }}>Asignar a</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {Object.entries(RUBROS_META).map(([key, m]) => (
              <button key={key} onClick={() => setRubro(key)} style={{
                background: rubro === key ? `${m.color}22` : "#13131f",
                border: `1px solid ${rubro === key ? m.color : "#2a2a3e"}`,
                borderRadius: 7, padding: "8px 12px", cursor: "pointer",
                color: rubro === key ? m.color : "#555",
                fontSize: 12, fontWeight: 600, textAlign: "left",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span>{m.icon}</span> {m.label}
                {rubroSugerido === key && (
                  <span style={{ marginLeft: "auto", color: "#4caf50",
                    fontSize: 9 }}>sugerido</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Lista de vales */}
        <div>
          <div style={{ color: "#aaa", fontSize: 11, textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 8 }}>
            Vales disponibles ({vales?.vales?.length || 0})
          </div>
          {!vales ? (
            <div style={{ color: "#555", padding: 16, textAlign: "center",
              fontSize: 12 }}>Cargando...</div>
          ) : vales.vales.length === 0 ? (
            <div style={{ color: "#555", padding: 16, textAlign: "center",
              fontSize: 12 }}>Sin vales de décimo disponibles para asignar</div>
          ) : (
            <div style={{ maxHeight: 240, overflowY: "auto",
              display: "flex", flexDirection: "column", gap: 4 }}>
              {vales.vales.map(v => (
                <div key={v.id} onClick={() => setSelected(v.id)} style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center",
                  background: selected === v.id ? "#001a2a" : "#13131f",
                  border: `1px solid ${selected === v.id ? "#4cc9f0" : "#1e1e2e"}`,
                  borderRadius: 7, padding: "10px 12px", cursor: "pointer",
                }}>
                  <div>
                    <div style={{ color: "#ccc", fontSize: 12 }}>
                      {v.descripcion || `Vale #${v.numero}`}
                    </div>
                    <div style={{ color: "#555", fontSize: 10, marginTop: 2 }}>
                      Vale #{v.numero} · {v.fecha}
                      {v.rubro_sugerido && (
                        <span style={{ color: "#4caf50", marginLeft: 6 }}>
                          → {RUBROS_META[v.rubro_sugerido]?.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ color: "#4cc9f0", fontFamily: "monospace",
                    fontWeight: 700, fontSize: 14 }}>${fmt(v.valor)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            background: "none", border: "1px solid #2a2a3e",
            borderRadius: 7, color: "#aaa", padding: "8px 18px",
            cursor: "pointer", fontSize: 12,
          }}>Cancelar</button>
          <button onClick={handleAsignar} disabled={!selected || saving} style={{
            background: selected ? "#001a2a" : "#13131f",
            border: `1px solid ${selected ? "#4cc9f0" : "#2a2a3e"}`,
            borderRadius: 7, color: selected ? "#4cc9f0" : "#555",
            padding: "8px 20px", cursor: selected ? "pointer" : "default",
            fontSize: 12, fontWeight: 600,
            opacity: saving ? 0.6 : 1,
          }}>
            {saving ? "Asignando..." : "✅ Asignar"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Modal pago manual ────────────────────────────────────────────────────────
function ModalPagoManual({ cedula, nombre, anio, rubro, valorPendiente,
  onClose, onGuardado }) {
  const meta = RUBROS_META[rubro] || {};
  const [form, setForm] = useState({
    descripcion: "", valor: String(valorPendiente || ""),
    forma_pago: "cheque", referencia: "",
  });
  const [saving, setSaving] = useState(false);

  async function handleGuardar() {
    if (!form.valor || parseFloat(form.valor) <= 0) return;
    setSaving(true);
    await api.post("/empleados/rubros/pago", {
      cedula, anio, rubro,
      descripcion: form.descripcion || `Pago ${meta.label}`,
      valor:       parseFloat(form.valor),
      forma_pago:  form.forma_pago,
      referencia:  form.referencia,
    });
    setSaving(false);
    onGuardado();
    onClose();
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)", zIndex: 200 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "#1e1e2e", borderRadius: 12, padding: 28,
        width: 420, zIndex: 201, border: "1px solid #2a2a3e",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        display: "flex", flexDirection: "column", gap: 16,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center" }}>
          <div>
            <div style={{ color: meta.color, fontWeight: 700, fontSize: 15 }}>
              {meta.icon} {meta.label}
            </div>
            <div style={{ color: "#555", fontSize: 12, marginTop: 2 }}>
              {nombre} · Año {anio}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: "#555", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {/* Forma de pago */}
        <div>
          <div style={{ color: "#aaa", fontSize: 11, textTransform: "uppercase",
            letterSpacing: 1, marginBottom: 8 }}>Forma de pago</div>
          <div style={{ display: "flex", gap: 6 }}>
            {FORMAS_PAGO.map(fp => (
              <button key={fp.key} onClick={() => setForm(f => ({...f, forma_pago: fp.key}))}
                style={{
                  flex: 1, padding: "8px 4px", borderRadius: 7, cursor: "pointer",
                  fontSize: 10, fontWeight: 600,
                  background: form.forma_pago === fp.key ? "#1a2a3e" : "#13131f",
                  border: `1px solid ${form.forma_pago === fp.key ? "#4361ee" : "#2a2a3e"}`,
                  color: form.forma_pago === fp.key ? "#4361ee" : "#555",
                }}>
                <div style={{ fontSize: 14 }}>{fp.icon}</div>
                {fp.label}
              </button>
            ))}
          </div>
        </div>

        {/* Campos */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <div style={{ color: "#555", fontSize: 10, marginBottom: 4 }}>VALOR $</div>
              <input type="number" value={form.valor}
                onChange={e => setForm(f => ({...f, valor: e.target.value}))}
                style={inputStyle} />
            </div>
            <div>
              <div style={{ color: "#555", fontSize: 10, marginBottom: 4 }}>
                {form.forma_pago === "cheque" ? "N° CHEQUE" : "REFERENCIA"}
              </div>
              <input type="text" value={form.referencia}
                placeholder={form.forma_pago === "cheque" ? "001234" : "opcional"}
                onChange={e => setForm(f => ({...f, referencia: e.target.value}))}
                style={inputStyle} />
            </div>
          </div>
          <div>
            <div style={{ color: "#555", fontSize: 10, marginBottom: 4 }}>DESCRIPCIÓN</div>
            <input type="text" value={form.descripcion}
              placeholder={`Pago ${meta.label} ${anio}`}
              onChange={e => setForm(f => ({...f, descripcion: e.target.value}))}
              style={inputStyle} />
          </div>
        </div>

        {valorPendiente > 0 && (
          <div style={{ background: "#13131f", borderRadius: 7,
            padding: "8px 12px", display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#555", fontSize: 11 }}>Pendiente por pagar</span>
            <span style={{ color: meta.color, fontFamily: "monospace",
              fontWeight: 700 }}>${fmt(valorPendiente)}</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            background: "none", border: "1px solid #2a2a3e",
            borderRadius: 7, color: "#aaa", padding: "8px 18px",
            cursor: "pointer", fontSize: 12,
          }}>Cancelar</button>
          <button onClick={handleGuardar} disabled={saving} style={{
            background: "#1a3a2a", border: "1px solid #4caf50",
            borderRadius: 7, color: "#4caf50", padding: "8px 20px",
            cursor: "pointer", fontSize: 12, fontWeight: 600,
            opacity: saving ? 0.6 : 1,
          }}>
            {saving ? "Guardando..." : "💾 Guardar pago"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Drawer pagos de una celda ────────────────────────────────────────────────
function DrawerPagos({ cedula, nombre, rubro, anio, rubroData, onClose, onRefresh }) {
  const meta = RUBROS_META[rubro] || {};
  const [eliminando, setEliminando] = useState(null);

  async function handleEliminar(id) {
    if (!window.confirm("¿Eliminar este pago?")) return;
    setEliminando(id);
    await api.delete(`/empleados/rubros/pago/${id}`);
    setEliminando(null);
    onRefresh();
  }

  const forma_icon = { vale_caja: "🧾", cheque: "📄",
    transferencia: "🔀", efectivo: "💵" };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.4)", zIndex: 100 }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 380,
        background: "#1e1e2e", zIndex: 101,
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.5)",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #2a2a3e" }}>
          <div style={{ display: "flex", justifyContent: "space-between",
            alignItems: "flex-start" }}>
            <div>
              <div style={{ color: meta.color, fontWeight: 700, fontSize: 15 }}>
                {meta.icon} {meta.label} {anio}
              </div>
              <div style={{ color: "#aaa", fontSize: 13, marginTop: 2 }}>{nombre}</div>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none",
              color: "#555", fontSize: 20, cursor: "pointer" }}>✕</button>
          </div>

          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8, marginTop: 14 }}>
            {[
              ["Total",     rubroData?.valor_total || 0, "#fff"],
              ["Pagado",    rubroData?.pagado || 0,      "#4caf50"],
              ["Pendiente", rubroData?.pendiente || 0,   meta.color],
            ].map(([label, val, color]) => (
              <div key={label} style={{ background: "#13131f", borderRadius: 7,
                padding: "8px 10px", textAlign: "center" }}>
                <div style={{ color: "#555", fontSize: 9,
                  textTransform: "uppercase" }}>{label}</div>
                <div style={{ color, fontFamily: "monospace",
                  fontWeight: 700, fontSize: 14, marginTop: 3 }}>${fmt(val)}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8 }}>
            <BarraProgreso
              pagado={rubroData?.pagado || 0}
              total={rubroData?.valor_total || 0}
              color={meta.color}
            />
          </div>
        </div>

        {/* Lista de pagos */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {(rubroData?.pagos || []).length === 0 ? (
            <div style={{ color: "#555", textAlign: "center",
              padding: 30, fontSize: 12 }}>Sin pagos registrados</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(rubroData?.pagos || []).map((p, i) => (
                <div key={p.id || i} style={{
                  background: "#13131f", borderRadius: 8,
                  padding: "10px 12px",
                  border: "1px solid #1a1a2e",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 14 }}>
                          {forma_icon[p.forma_pago] || "💵"}
                        </span>
                        <span style={{ color: "#ccc", fontSize: 12 }}>
                          {p.descripcion || "Pago"}
                        </span>
                      </div>
                      <div style={{ color: "#555", fontSize: 10, marginTop: 3 }}>
                        {p.created_at}
                        {p.referencia && (
                          <span style={{ marginLeft: 6, color: "#444" }}>
                            #{p.referencia}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: "#4caf50", fontFamily: "monospace",
                        fontWeight: 700, fontSize: 14 }}>${fmt(p.valor)}</span>
                      <button
                        disabled={eliminando === p.id}
                        onClick={() => handleEliminar(p.id)}
                        style={{ background: "none", border: "none",
                          color: "#555", cursor: "pointer",
                          fontSize: 14, lineHeight: 1, padding: 0 }}>
                        {eliminando === p.id ? "..." : "✕"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── TabRubrosAnuales — componente principal ──────────────────────────────────
export function TabRubrosAnuales() {
  const anioActual = new Date().getFullYear();
  const [anio,     setAnio]     = useState(anioActual);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);

  // Modales
  const [modalVale,   setModalVale]   = useState(null); // {cedula, nombre}
  const [modalManual, setModalManual] = useState(null); // {cedula, nombre, rubro, pendiente}
  const [drawer,      setDrawer]      = useState(null); // {cedula, nombre, rubro, rubroData}

  const cargar = useCallback(() => {
    setLoading(true);
    api.get(`/empleados/rubros?anio=${anio}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [anio]);

  useEffect(() => { cargar(); }, [cargar]);

  const rubrosOrden = ["decimo_cuarto", "decimo_tercero", "utilidades", "vacaciones"];

  // KPIs globales
  const kpis = data ? rubrosOrden.map(r => {
    const meta   = RUBROS_META[r];
    const total  = (data.empleados || []).reduce((s, e) => s + (e.rubros[r]?.valor_total || 0), 0);
    const pagado = (data.empleados || []).reduce((s, e) => s + (e.rubros[r]?.pagado || 0), 0);
    return { rubro: r, meta, total, pagado, pendiente: total - pagado };
  }) : [];

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", color: "#e0e0e0" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>
            Rubros Anuales
          </div>
          <div style={{ color: "#555", fontSize: 12, marginTop: 2 }}>
            Décimos · Utilidades · Vacaciones
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {[anioActual - 1, anioActual, anioActual + 1].map(a => (
            <button key={a} onClick={() => setAnio(a)} style={{
              background: anio === a ? "#1a2a3e" : "#1e1e2e",
              border: `1px solid ${anio === a ? "#4361ee" : "#2a2a3e"}`,
              color: anio === a ? "#4361ee" : "#555",
              borderRadius: 7, padding: "6px 14px", fontSize: 13,
              fontWeight: anio === a ? 700 : 400, cursor: "pointer",
            }}>{a}</button>
          ))}
          <button onClick={cargar} style={{
            background: "#1e1e2e", border: "1px solid #2a2a3e",
            color: "#555", borderRadius: 7, padding: "6px 12px",
            fontSize: 12, cursor: "pointer",
          }}>🔄</button>
        </div>
      </div>

      {/* KPIs por rubro */}
      {!loading && data && (
        <div style={{ display: "grid",
          gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
          {kpis.map(k => (
            <div key={k.rubro} style={{
              background: "#1e1e2e", borderRadius: 10,
              padding: "14px 16px", borderTop: `3px solid ${k.meta.color}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6,
                marginBottom: 8 }}>
                <span style={{ fontSize: 16 }}>{k.meta.icon}</span>
                <span style={{ color: "#aaa", fontSize: 11,
                  fontWeight: 600 }}>{k.meta.label}</span>
                <span style={{ marginLeft: "auto", color: "#555",
                  fontSize: 9 }}>{k.meta.mes}</span>
              </div>
              <div style={{ color: k.meta.color, fontFamily: "monospace",
                fontWeight: 700, fontSize: 18 }}>${fmt(k.pagado)}</div>
              <div style={{ color: "#555", fontSize: 10, marginBottom: 6 }}>
                de ${fmt(k.total)}
              </div>
              <BarraProgreso pagado={k.pagado} total={k.total} color={k.meta.color} />
            </div>
          ))}
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <div style={{ color: "#555", textAlign: "center", padding: 40 }}>
          Cargando...
        </div>
      ) : !data?.empleados?.length ? (
        <div style={{ color: "#555", textAlign: "center", padding: 40 }}>
          Sin datos para {anio}
        </div>
      ) : (
        <div style={{ background: "#1e1e2e", borderRadius: 12, overflow: "hidden" }}>
          {/* Header tabla */}
          <div style={{ display: "grid",
            gridTemplateColumns: `200px repeat(${rubrosOrden.length}, 1fr)`,
            background: "#13131f", padding: "10px 16px", gap: 8 }}>
            <div style={{ color: "#555", fontSize: 11,
              textTransform: "uppercase" }}>Empleado</div>
            {rubrosOrden.map(r => {
              const m = RUBROS_META[r];
              return (
                <div key={r} style={{ textAlign: "center" }}>
                  <div style={{ color: m.color, fontSize: 11,
                    fontWeight: 700 }}>{m.icon} {m.label}</div>
                  <div style={{ color: "#444", fontSize: 9 }}>{m.mes}</div>
                </div>
              );
            })}
          </div>

          {/* Filas por empleado */}
          {data.empleados.map((emp, i) => (
            <div key={emp.cedula} style={{
              display: "grid",
              gridTemplateColumns: `200px repeat(${rubrosOrden.length}, 1fr)`,
              padding: "14px 16px", gap: 8, alignItems: "center",
              background: i % 2 === 0 ? "#1e1e2e" : "#1a1a2e",
              borderBottom: "1px solid #13131f",
            }}>
              {/* Nombre empleado */}
              <div>
                <div style={{ color: "#fff", fontSize: 13,
                  fontWeight: 600 }}>{emp.nombre_corto}</div>
                <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                  <button
                    onClick={() => setModalVale({ cedula: emp.cedula, nombre: emp.nombre_corto })}
                    style={{
                      background: "#001a2a", border: "1px solid #4cc9f044",
                      color: "#4cc9f0", borderRadius: 4, padding: "2px 7px",
                      fontSize: 9, cursor: "pointer", fontWeight: 600,
                    }}>🧾 Asignar vale</button>
                </div>
              </div>

              {/* Celdas por rubro */}
              {rubrosOrden.map(r => {
                const rd   = emp.rubros[r];
                const meta = RUBROS_META[r];
                if (!rd) return (
                  <div key={r} style={{ textAlign: "center" }}>
                    <button
                      onClick={() => setModalManual({
                        cedula: emp.cedula, nombre: emp.nombre_corto,
                        rubro: r, pendiente: 0,
                      })}
                      style={{
                        background: "#13131f", border: "1px dashed #2a2a3e",
                        color: "#444", borderRadius: 6, padding: "4px 8px",
                        fontSize: 10, cursor: "pointer",
                      }}>+ Agregar</button>
                  </div>
                );

                return (
                  <div key={r} style={{ textAlign: "center" }}>
                    {/* Totales */}
                    <div style={{ marginBottom: 4 }}>
                      <span style={{
                        color: rd.completo ? "#4caf50" : meta.color,
                        fontFamily: "monospace", fontWeight: 700, fontSize: 13,
                      }}>
                        ${fmt(rd.pagado)}
                      </span>
                      <span style={{ color: "#444", fontSize: 10 }}>
                        {" / "}${fmt(rd.valor_total)}
                      </span>
                    </div>

                    {/* Barra */}
                    <BarraProgreso pagado={rd.pagado} total={rd.valor_total} color={meta.color} />

                    {/* Botones */}
                    <div style={{ display: "flex", gap: 3, marginTop: 6,
                      justifyContent: "center" }}>
                      {rd.num_pagos > 0 && (
                        <button
                          onClick={() => setDrawer({
                            cedula: emp.cedula,
                            nombre: emp.nombre_corto,
                            rubro: r,
                            rubroData: rd,
                          })}
                          style={{
                            background: "#1a2a1a", border: "1px solid #4caf5033",
                            color: "#4caf50", borderRadius: 4,
                            padding: "2px 7px", fontSize: 9, cursor: "pointer",
                          }}>
                          📋 {rd.num_pagos} pago{rd.num_pagos > 1 ? "s" : ""}
                        </button>
                      )}
                      {!rd.completo && (
                        <button
                          onClick={() => setModalManual({
                            cedula: emp.cedula,
                            nombre: emp.nombre_corto,
                            rubro: r,
                            pendiente: rd.pendiente,
                          })}
                          style={{
                            background: "#13131f",
                            border: `1px solid ${meta.color}33`,
                            color: meta.color, borderRadius: 4,
                            padding: "2px 7px", fontSize: 9, cursor: "pointer",
                          }}>+ Pago</button>
                      )}
                      {rd.completo && (
                        <span style={{
                          background: "#0d2218", color: "#4caf50",
                          borderRadius: 4, padding: "2px 7px",
                          fontSize: 9, fontWeight: 700,
                        }}>✅ Completo</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Modales */}
      {modalVale && (
        <ModalAsignarVale
          cedula={modalVale.cedula}
          nombre={modalVale.nombre}
          anio={anio}
          onClose={() => setModalVale(null)}
          onAsignado={cargar}
        />
      )}

      {modalManual && (
        <ModalPagoManual
          cedula={modalManual.cedula}
          nombre={modalManual.nombre}
          anio={anio}
          rubro={modalManual.rubro}
          valorPendiente={modalManual.pendiente}
          onClose={() => setModalManual(null)}
          onGuardado={cargar}
        />
      )}

      {drawer && (
        <DrawerPagos
          cedula={drawer.cedula}
          nombre={drawer.nombre}
          rubro={drawer.rubro}
          anio={anio}
          rubroData={drawer.rubroData}
          onClose={() => setDrawer(null)}
          onRefresh={cargar}
        />
      )}
    </div>
  );
}

// Estilos compartidos
const inputStyle = {
  background: "#13131f", border: "1px solid #2a2a3e",
  borderRadius: 6, color: "#fff", padding: "8px 10px",
  fontSize: 12, outline: "none", width: "100%",
  boxSizing: "border-box",
};
